import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import clsx from "clsx";

import styles from "../../styles/tinymce.module.css";

import { apiGetMinutesDraft } from "../../lib/api/session";
import {
  generateMotionWording,
  getGroupForTitleEngage,
  getItemNumber,
  getGroupForTitle,
} from "../../helpers/text";
import {
  // getSubheadingByGroupId,
  getMinutesInfoByGroupId,
  getNameByGroupId,
} from "../../helpers/arrays";
import { textToHtmlPrint, textToHtmlTinyMCE } from "../../helpers/converter";

import EditorHTML from "../RichText/EditorHTML";
import MinutesRender from "../TinyMce/MinutesRender";

function isStringifiedObject(str) {
  if (typeof str !== "string") return false;

  try {
    const parsed = JSON.parse(str);
    return parsed && typeof parsed === "object";
  } catch {
    return false;
  }
}

const MinutesPrint = () => {
  const router = useRouter();
  const { id } = router.query;
  const [initialState, setInitialState] = useState();
  const [groupOrder, setGroupOrder] = useState();

  useEffect(() => {
    if (id) {
      async function fetchData() {
        const result = await apiGetMinutesDraft(id);
        if (result) {
          setInitialState(result);
          setGroupOrder(result.groupOrder);
        } else {
          console.error("no result");
        }
      }
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <div className="h-full w-full mt-1 max-w-5xl mx-auto bg-white py-4 px-10 overflow-y-auto">
      {initialState && (
        <>
          <MinutesRender meeting={initialState} />
          {/* List */}
          <div className="">
            {groupOrder &&
              groupOrder.map((group, index) => {
                const { bulkVote } = group;
                // const subheading = getSubheadingByGroupId(
                //   group._id,
                //   initialState.groupSubheadings
                // );
                const minutesInfo = getMinutesInfoByGroupId(
                  group._id,
                  initialState.groupSubheadings,
                );
                const name = getNameByGroupId(
                  group._id,
                  initialState.groupSubheadings,
                  group.name,
                );
                const groupActions = initialState.actionTopics
                  .map((topic) => {
                    if (
                      topic.agendaGroup.id.toString() === group._id.toString()
                    ) {
                      const motionWithVoteNumber = topic.motions?.find(
                        (motion) => motion.useVoteNumber === true,
                      );

                      let voteNumber = motionWithVoteNumber?.voteNumber ?? "";

                      if (
                        initialState?.clientId === "tarrantcountytx" &&
                        voteNumber
                      ) {
                        voteNumber = `Court Order Number ${voteNumber.substring(
                          1,
                        )}`;
                      }

                      return {
                        ...topic,
                        voteNumber,
                      };
                    }
                    return null;
                  })
                  .filter((topic) => topic !== null);
                groupActions.sort((a, b) => {
                  if (a.bulkTopic === true) return -1; // Move the entry with bulkTopic: true to the front
                  if (b.bulkTopic === true) return 1; // Move the entry with bulkTopic: true to the front
                  return a.order - b.order; // Sort by the order property for other entries
                });
                // console.log(name);
                return (
                  <div
                    key={`group-${index}`}
                    className="mb-3 lg:mb-4 xl:mb-8 print-ready"
                  >
                    <div className="border-b border-gray-800">
                      <p className="text-black font-bold">
                        {getGroupForTitleEngage(
                          initialState?.groupNumbering,
                          index,
                          initialState?.groupOrder,
                          initialState?.childNumbering,
                        )}{" "}
                        {name}
                      </p>
                    </div>
                    {/* <p className="text-black italic pt-1 ml-2 whitespace-pre-line">
                      {subheading}
                    </p> */}
                    {minutesInfo.length > 0 &&
                      isStringifiedObject(minutesInfo) && (
                        <EditorHTML value={minutesInfo} />
                      )}
                    {minutesInfo.length > 0 &&
                      !isStringifiedObject(minutesInfo) && (
                        <div className={`mb-4 ${styles.tinyContainer}`}>
                          {textToHtmlTinyMCE(minutesInfo)}
                        </div>
                      )}
                    {groupActions.map((topic, i) => {
                      if (topic.bulkTopic) {
                        return (
                          <div className="mt-1 mb-3" key={`motion-${i}`}>
                            {topic.minutesInfo.length > 0 &&
                              topic.minutesEditor === 1 && (
                                <EditorHTML value={topic.minutesInfo} />
                              )}
                            {topic.minutesInfo.length > 0 &&
                              topic.minutesEditor === 2 && (
                                <div className={`mb-4 ${styles.tinyContainer}`}>
                                  {textToHtmlTinyMCE(topic.minutesInfo)}
                                </div>
                              )}
                            {topic.motions.map((motion, mIdx) => {
                              let insertedText = "";
                              if (motion.overWriteDefault) {
                                insertedText = motion.wording;
                              } else {
                                insertedText = generateMotionWording(
                                  initialState.board.motionText,
                                  motion,
                                  topic,
                                );
                              }
                              return (
                                <div
                                  className="mt-1 font-medium whitespace-pre-line"
                                  key={`group-${index}-motion-${mIdx}`}
                                >
                                  <p className="">{insertedText}</p>
                                  {!motion.overWriteDefault &&
                                    motion.wording.length > 0 && (
                                      <p className="italic">{motion.wording}</p>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      } else {
                        let iDx = i;
                        if (topic.agendaGroup.bulkVote) {
                          iDx--;
                        }
                        return (
                          <div key={`group-${index}-topic-${i}`}>
                            <div
                              className={clsx(
                                i === 0 ? "mt-4" : "",
                                "ml-4 xl:ml-8 mb-6 text-black",
                              )}
                            >
                              <p className="font-semibold">
                                {topic.voteNumber && (
                                  <span
                                    className={clsx(
                                      initialState?.clientId ===
                                        "tarrantcountytx"
                                        ? "font-bold"
                                        : "font-medium",
                                      "italic",
                                    )}
                                  >
                                    &#40;{topic.voteNumber}&#41;{" "}
                                  </span>
                                )}
                                {getGroupForTitle(
                                  initialState?.groupNumbering,
                                  index,
                                  initialState?.groupOrder,
                                  initialState?.childNumbering,
                                )}
                                {getItemNumber(
                                  initialState.actionTopics,
                                  initialState.itemNumbering,
                                  topic,
                                  iDx,
                                  initialState?.groupNumbering,
                                  initialState?.childNumbering,
                                )}
                                {!topic.hideTimeStamp && (
                                  <span className="font-normal">
                                    - {topic.startTime}
                                  </span>
                                )}
                              </p>
                              {topic.votedByBoard && (
                                <p className="text-xs lg:text-sm italic">
                                  &#40;{topic.votedByBoard?.name}&#41;
                                </p>
                              )}
                              {!topic.hideDescription && topic.topicBlurb && (
                                <span className="whitespace-pre-line font-medium ml-2">
                                  {topic.topicBlurb}
                                </span>
                              )}
                              {!topic.hideSubItems &&
                                topic.subTopics?.length > 0 &&
                                topic.subTopics.map((sub, sIdx) => {
                                  return (
                                    <p
                                      key={`sub-${sIdx}`}
                                      className="ml-8 py-0.5 font-medium"
                                    >
                                      &#8212; {sub}
                                    </p>
                                  );
                                })}
                              {topic.publicHearing.openTime !== null && (
                                <p className="">
                                  Public Hearing was opened at{" "}
                                  {topic.publicHearing.openTime}
                                  {topic.publicHearing.closeTime !== null &&
                                    ` and closed at ${topic.publicHearing.closeTime}`}
                                </p>
                              )}
                              {topic.removedFromConsent && (
                                <div className="mt-1">
                                  <p
                                    className={clsx(
                                      "whitespace-pre-line ml-2 text-red-600",
                                    )}
                                  >
                                    &#x2a;&#x2a; Item was Removed from Consent
                                  </p>
                                </div>
                              )}
                              {topic.minutesInfo.length > 0 &&
                                topic.minutesEditor === 1 && (
                                  <EditorHTML value={topic.minutesInfo} />
                                )}
                              {topic.minutesInfo.length > 0 &&
                                topic.minutesEditor === 2 && (
                                  <div
                                    className={`mb-4 ${styles.tinyContainer}`}
                                  >
                                    {textToHtmlTinyMCE(topic.minutesInfo)}
                                  </div>
                                )}
                              {!bulkVote &&
                                !topic.agendaGroup?.parentGroup?.bulkVote &&
                                topic.motions.length > 0 && (
                                  <div className="mt-1">
                                    {topic.motions.map((motion, mIdx) => {
                                      let insertedText = "";
                                      if (motion.overWriteDefault) {
                                        insertedText = motion.wording;
                                      } else {
                                        insertedText = generateMotionWording(
                                          initialState.board.motionText,
                                          motion,
                                          topic,
                                        );
                                      }
                                      return (
                                        <div
                                          className="mt-1 font-medium whitespace-pre-line"
                                          key={`group-${index}-motion-${mIdx}`}
                                        >
                                          <p className="">{insertedText}</p>
                                          {!motion.overWriteDefault &&
                                            motion.wording.length > 0 && (
                                              <p className="italic">
                                                {motion.wording}
                                              </p>
                                            )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              {(bulkVote ||
                                topic.agendaGroup?.parentGroup?.bulkVote) &&
                                (topic.removedFromConsent ||
                                  initialState?.clientId ===
                                    "tarrantcountytx") &&
                                topic.motions.length > 0 && (
                                  <div className="mt-1">
                                    {topic.motions.map((motion, mIdx) => {
                                      let insertedText = "";
                                      if (motion.overWriteDefault) {
                                        insertedText = motion.wording;
                                      } else {
                                        insertedText = generateMotionWording(
                                          initialState.board.motionText,
                                          motion,
                                          topic,
                                        );
                                      }
                                      return (
                                        <div
                                          className="mt-1 font-medium whitespace-pre-line"
                                          key={`group-${index}-motion-${mIdx}`}
                                        >
                                          <p className="font-semibold text-red-600">
                                            &#x2a;&#x2a; Item was Removed from
                                            Consent
                                          </p>
                                          <p className="">{insertedText}</p>
                                          {!motion.overWriteDefault &&
                                            motion.wording.length > 0 && (
                                              <p className="italic">
                                                {motion.wording}
                                              </p>
                                            )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                            </div>
                          </div>
                        );
                      }
                    })}
                  </div>
                );
              })}
          </div>
          {/* Footer */}
          <div className="mt-4">
            {textToHtmlPrint(initialState.pdfMinutes?.motionFooterNote)}
          </div>
        </>
      )}
      <style>{`
  @media print {
    /* Ensure page breaks work properly */
    .mce-pagebreak {
      page-break-before: always;
      border: 0 !important;
      height: 0 !important;
      margin: 0 !important;
    }
  }
`}</style>
    </div>
  );
};

export default MinutesPrint;
